import React, { useEffect, useState, createElement } from 'react';
import { ShoppingBagIcon } from 'lucide-react';
import { ProductSelector } from './components/ProductSelector';
import { ColorSelector } from './components/ColorSelector';
import { ProductDisplay } from './components/ProductDisplay';
import { ViewToggle } from './components/ViewToggle';
import { ImageUploader } from './components/ImageUploader';
import { DesignControls } from './components/DesignControls';
import { DebugPanel } from './components/DebugPanel';
import { ArtworkLibrary, Artwork, ArtworkVersion } from './components/ArtworkLibrary';
import { PrintSizeModal } from './components/PrintSizeModal';
import { AddToBagModal } from './components/AddToBagModal';
import { ShoppingBag, ShoppingBagItem } from './components/ShoppingBag';
import { Checkout } from './components/Checkout';
const REMOVEBG_API_KEY = 'qa8wvT66hnF8GJUvm7YGJdth';
const SAMPLE_ARTWORKS = [{
  url: 'https://cdn.magicpatterns.com/uploads/orWTEcxiEbwyo6cawSXCdh/Shane_merem_Create_a_mock_up_logo_that_I_can_use_on_a_white_background_vector_83210698-a8fa-4d77-8346-3d6074e66d38_-_Magnet_Crop_2025-11-27_at_20.34.31-gigapixel-cgi-1500w.png',
  name: 'Explore Mountain Logo'
}, {
  url: 'https://cdn.magicpatterns.com/uploads/wAbWpryKUDmiyaEcvqUWo6/Shane_merem_Create_a_mock_up_logo_that_I_can_use_on_a_white_background_vector_9616d1af-edda-4469-b4e4-d1ae0d9970e0-gigapixel-cgi-1500w.png',
  name: 'Peak Proof Logo'
}, {
  url: 'https://cdn.magicpatterns.com/uploads/sxA5h2PbRofPG3YG3mFw6k/Shane_merem_Create_a_mock_up_logo_that_I_can_use_on_a_white_background_vector_67106283-ad71-4152-b0c1-28a40c5b59bc-gigapixel-cgi-1500w.png',
  name: 'Apparel Logo Mark'
}, {
  url: 'https://cdn.magicpatterns.com/uploads/n4U5HvCidEnQSqSakqZsP2/Shane_merem_Create_a_mock_up_logo_that_I_can_use_on_a_white_background_vector_83210698-a8fa-4d77-8346-3d6074e66d38-gigapixel-cgi-1500w.png',
  name: 'Explore Badge'
}, {
  url: 'https://cdn.magicpatterns.com/uploads/5Auj49N3yz6SCDDZdaeQW5/Shane_merem_Create_a_mock_up_logo_that_I_can_use_on_a_white_background_vector_f51f0631-c27f-4947-8dd6-bb941715959d-gigapixel-cgi-1500w.png',
  name: 'Oceanbound Logo'
}];
interface ColorOption {
  id: string;
  name: string;
  hex: string;
}
interface ProductVariant {
  colorId: string;
  imageUrl: string;
  backImageUrl?: string;
}
interface Product {
  id: string;
  name: string;
  icon: string;
  colors: ColorOption[];
  variants: ProductVariant[];
  hasBackView: boolean;
}
interface Design {
  id: string;
  image: string;
  x: number;
  y: number;
  scale: number;
  zIndex: number;
  artworkId?: string;
  printSize?: number;
  resolutionFixAuthorized?: boolean;
}
interface DesignState {
  front: Design[];
  back: Design[];
}
interface DebugLog {
  id: string;
  timestamp: Date;
  type: 'info' | 'success' | 'error' | 'data';
  message: string;
  data?: any;
}
const products: Product[] = [{
  id: 'tshirt',
  name: 'T-Shirt',
  icon: '👕',
  hasBackView: true,
  colors: [{
    id: 'black',
    name: 'Black',
    hex: '#1a1a1a'
  }, {
    id: 'white',
    name: 'White',
    hex: '#ffffff'
  }],
  variants: [{
    colorId: 'black',
    imageUrl: 'https://uploadthingy.s3.us-west-1.amazonaws.com/mEeUcXERMrDFLxdYQtY8gc/Shane_merem_Create_a_blank_T-shirt_that%E2%80%99s_black_to_be_used_as_a_template_to_d_fdc89b0d-de77-4600-b66c-78c19650b062.png',
    backImageUrl: 'https://uploadthingy.s3.us-west-1.amazonaws.com/tkfoigtk2ZB9MUbqCPLRKA/Shane_merem_Show_me_the_back_of_this_T-shirt_c932309c-6550-4a8e-b0fe-b8d58123cb82.png'
  }, {
    colorId: 'white',
    imageUrl: 'https://uploadthingy.s3.us-west-1.amazonaws.com/x7uH4vzUGSzK4Aoobc4UUX/Shane_merem_Keep_this_shirt%2C_exactly_the_same%2C_except_change_it_to_a_white_T-_e07ce4c9-6cd9-4500-8acd-f88b8b391d82.png',
    backImageUrl: 'https://uploadthingy.s3.us-west-1.amazonaws.com/8RktkkYJcZVjCc5L7EF1id/Shane_merem_Show_the_back_of_the_shirt_e8fb1d9c-a1c6-4864-8a3d-40e76521dbd8.png'
  }]
}, {
  id: 'hoodie',
  name: 'Hoodie',
  icon: '🧥',
  hasBackView: true,
  colors: [{
    id: 'black',
    name: 'Black',
    hex: '#1a1a1a'
  }, {
    id: 'white',
    name: 'White',
    hex: '#ffffff'
  }],
  variants: [{
    colorId: 'black',
    imageUrl: 'https://uploadthingy.s3.us-west-1.amazonaws.com/1NLkzd7inLKDZxxyCnLMVQ/Shane_merem_Create_a_blank_hoodie_black_that_will_be_used_as_a_template_to_de_9da7fda6-0693-4ff8-8d33-11b43eeedb9a.png',
    backImageUrl: 'https://uploadthingy.s3.us-west-1.amazonaws.com/tXYWqBnXMu8yVjcmngHYNB/Shane_merem_Show_the_back_of_the_hoodie_92dd0a70-f0ef-430c-9253-f8203e58b5b8.png'
  }, {
    colorId: 'white',
    imageUrl: 'https://uploadthingy.s3.us-west-1.amazonaws.com/6GGCsyKoXDaBMweGi4YGHb/Shane_merem_Keep_this_hoodie%2C_exactly_the_same%2C_except_change_it_to_white_e471544b-1475-4888-b61b-cc658457268b.png',
    backImageUrl: 'https://uploadthingy.s3.us-west-1.amazonaws.com/dtz8Y1aEPhLX3DSiBm1pJs/Shane_merem_Show_the_back_of_the_shirt_7cf9879d-7608-49e2-bb45-625ce0eaa496.png'
  }]
}, {
  id: 'cap',
  name: 'Trucker Cap',
  icon: '🧢',
  hasBackView: false,
  colors: [{
    id: 'cream',
    name: 'Cream',
    hex: '#f5f5dc'
  }, {
    id: 'black',
    name: 'Black',
    hex: '#1a1a1a'
  }],
  variants: [{
    colorId: 'cream',
    imageUrl: 'https://uploadthingy.s3.us-west-1.amazonaws.com/jQ4u58XMbxEWQ5SunYQXen/Shane_merem_Create_a_trucker_style_cap_that_will_be_a_blank_used_to_design_a_c9b02650-d09f-4eee-8f04-d9719098d482.png'
  }, {
    colorId: 'black',
    imageUrl: 'https://uploadthingy.s3.us-west-1.amazonaws.com/aKXDEJynv8kaPLMRjYK3ki/Shane_merem_Keep_this_hat%2C_exactly_the_same_only_make_it_black_with_white_in_da7577a2-fc85-4863-b17a-fdd6a47d0b6f.png'
  }]
}];
export function App() {
  const [selectedProductId, setSelectedProductId] = useState('tshirt');
  const [selectedColorId, setSelectedColorId] = useState('black');
  const [currentView, setCurrentView] = useState<'front' | 'back'>('front');
  const [designs, setDesigns] = useState<Record<string, DesignState>>({});
  const [selectedDesignId, setSelectedDesignId] = useState<string | null>(null);
  const [debugLogs, setDebugLogs] = useState<DebugLog[]>([]);
  const [isDebugMinimized, setIsDebugMinimized] = useState(false);
  const [artworkLibrary, setArtworkLibrary] = useState<Artwork[]>([]);
  const [showArtworkLibrary, setShowArtworkLibrary] = useState(false);
  const [printSizeModalOpen, setPrintSizeModalOpen] = useState(false);
  const [pendingDesign, setPendingDesign] = useState<{
    imageData: string;
    artworkId?: string;
  } | null>(null);
  const [addToBagModalOpen, setAddToBagModalOpen] = useState(false);
  const [shoppingBag, setShoppingBag] = useState<ShoppingBagItem[]>([]);
  const [showShoppingBag, setShowShoppingBag] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  useEffect(() => {
    const loadSampleArtworks = async () => {
      const sampleArtworks: Artwork[] = [];
      for (const sample of SAMPLE_ARTWORKS) {
        try {
          const response = await fetch(sample.url);
          const blob = await response.blob();
          const reader = new FileReader();
          const imageData = await new Promise<string>(resolve => {
            reader.onloadend = () => resolve(reader.result as string);
            reader.readAsDataURL(blob);
          });
          const dimensions = await getImageDimensions(imageData);
          const artwork: Artwork = {
            id: `sample-${Date.now()}-${Math.random()}`,
            name: sample.name,
            versions: [{
              versionNumber: 1,
              imageData,
              operation: 'upload',
              timestamp: new Date(),
              dimensions
            }],
            currentVersion: 1,
            createdAt: new Date()
          };
          sampleArtworks.push(artwork);
        } catch (error) {
          console.error(`Failed to load sample artwork: ${sample.name}`, error);
        }
      }
      setArtworkLibrary(sampleArtworks);
      addDebugLog('success', `Loaded ${sampleArtworks.length} sample artworks into library`);
    };
    loadSampleArtworks();
  }, []);
  const addDebugLog = (type: DebugLog['type'], message: string, data?: any) => {
    const log: DebugLog = {
      id: `${Date.now()}-${Math.random()}`,
      timestamp: new Date(),
      type,
      message,
      data
    };
    setDebugLogs(prev => [...prev, log]);
  };
  const getImageDimensions = (imageData: string): Promise<{
    width: number;
    height: number;
  }> => {
    return new Promise(resolve => {
      const img = new Image();
      img.onload = () => resolve({
        width: img.width,
        height: img.height
      });
      img.src = imageData;
    });
  };
  const createArtwork = async (imageData: string, name?: string): Promise<string> => {
    const dimensions = await getImageDimensions(imageData);
    const artworkId = `artwork-${Date.now()}-${Math.random()}`;
    const newArtwork: Artwork = {
      id: artworkId,
      name: name || `Artwork ${artworkLibrary.length + 1}`,
      versions: [{
        versionNumber: 1,
        imageData,
        operation: 'upload',
        timestamp: new Date(),
        dimensions
      }],
      currentVersion: 1,
      createdAt: new Date()
    };
    setArtworkLibrary(prev => [...prev, newArtwork]);
    addDebugLog('success', `Artwork added to library: ${newArtwork.name}`);
    return artworkId;
  };
  const addVersionToArtwork = async (artworkId: string, imageData: string, operation: ArtworkVersion['operation']) => {
    const dimensions = await getImageDimensions(imageData);
    setArtworkLibrary(prev => prev.map(artwork => {
      if (artwork.id === artworkId) {
        const newVersion: ArtworkVersion = {
          versionNumber: artwork.versions.length + 1,
          imageData,
          operation,
          timestamp: new Date(),
          dimensions
        };
        return {
          ...artwork,
          versions: [...artwork.versions, newVersion],
          currentVersion: newVersion.versionNumber
        };
      }
      return artwork;
    }));
    addDebugLog('success', `New version added to artwork (${operation})`);
  };
  const generateThumbnail = (): string => {
    const productKey = `${selectedProductId}-${selectedColorId}`;
    const allDesigns = designs[productKey];
    if (allDesigns?.front?.[0]) {
      return allDesigns.front[0].image;
    }
    if (allDesigns?.back?.[0]) {
      return allDesigns.back[0].image;
    }
    return currentVariant?.imageUrl || '';
  };
  const handleAddToBag = (designName: string) => {
    const productKey = `${selectedProductId}-${selectedColorId}`;
    const currentDesigns = designs[productKey] || {
      front: [],
      back: []
    };
    const bagItem: ShoppingBagItem = {
      id: `bag-${Date.now()}-${Math.random()}`,
      name: designName,
      productId: selectedProductId,
      productName: currentProduct.name,
      colorId: selectedColorId,
      colorName: currentColor?.name || '',
      designs: {
        front: currentDesigns.front || [],
        back: currentDesigns.back || []
      },
      sizeQuantities: {},
      thumbnail: generateThumbnail(),
      createdAt: new Date()
    };
    setShoppingBag(prev => [...prev, bagItem]);
    setAddToBagModalOpen(false);
    setShowShoppingBag(true);
    addDebugLog('success', `Added "${designName}" to shopping bag`);
  };
  const handleEditBagItem = (itemId: string) => {
    const item = shoppingBag.find(i => i.id === itemId);
    if (!item) return;
    setSelectedProductId(item.productId);
    setSelectedColorId(item.colorId);
    const productKey = `${item.productId}-${item.colorId}`;
    setDesigns(prev => ({
      ...prev,
      [productKey]: item.designs
    }));
    setShowShoppingBag(false);
    setCurrentView('front');
    addDebugLog('info', `Editing design: ${item.name}`);
  };
  const handleRemoveBagItem = (itemId: string) => {
    const item = shoppingBag.find(i => i.id === itemId);
    setShoppingBag(prev => prev.filter(i => i.id !== itemId));
    if (item) {
      addDebugLog('info', `Removed "${item.name}" from shopping bag`);
    }
  };
  const handleUpdateBagItemSizeQuantities = (itemId: string, sizeQuantities: {
    [size: string]: number;
  }) => {
    setShoppingBag(prev => prev.map(item => item.id === itemId ? {
      ...item,
      sizeQuantities
    } : item));
  };
  const handleProceedToCheckout = () => {
    setShowShoppingBag(false);
    setShowCheckout(true);
  };
  const handleOrderComplete = () => {
    setShoppingBag([]);
    setShowCheckout(false);
    addDebugLog('success', 'Order completed successfully!');
  };
  const currentProduct = products.find(p => p.id === selectedProductId)!;
  const currentVariant = currentProduct.variants.find(v => v.colorId === selectedColorId);
  const currentColor = currentProduct.colors.find(c => c.id === selectedColorId);
  const productKey = `${selectedProductId}-${selectedColorId}`;
  const currentDesigns = designs[productKey]?.[currentView] || [];
  const selectedDesign = currentDesigns.find(d => d.id === selectedDesignId);
  const hasAnyDesigns = (designs[productKey]?.front?.length || 0) + (designs[productKey]?.back?.length || 0) > 0;
  const handleProductChange = (productId: string) => {
    if (productId === selectedProductId) {
      return;
    }
    const currentProductKey = `${selectedProductId}-${selectedColorId}`;
    const hasDesigns = designs[currentProductKey]?.front && designs[currentProductKey].front.length > 0 || designs[currentProductKey]?.back && designs[currentProductKey].back.length > 0;
    if (hasDesigns) {
      const confirmed = window.confirm('⚠️ Switch Garment?\n\n' + 'You have designs on the current garment. ' + 'Your designs will be transferred to the new garment.\n\n' + 'Do you want to continue?');
      if (!confirmed) {
        return;
      }
      addDebugLog('info', `Switching garment from ${selectedProductId} to ${productId}`, {
        designsTransferred: {
          front: designs[currentProductKey]?.front?.length || 0,
          back: designs[currentProductKey]?.back?.length || 0
        }
      });
    }
    const newProduct = products.find(p => p.id === productId)!;
    const newColorId = newProduct.colors[0].id;
    const newProductKey = `${productId}-${newColorId}`;
    if (hasDesigns) {
      setDesigns(prev => {
        const currentDesigns = prev[currentProductKey] || {
          front: [],
          back: []
        };
        return {
          ...prev,
          [newProductKey]: {
            front: currentDesigns.front || [],
            back: currentDesigns.back || []
          }
        };
      });
      addDebugLog('success', 'Designs transferred to new garment successfully');
    }
    setSelectedProductId(productId);
    setSelectedColorId(newColorId);
    setCurrentView('front');
    setSelectedDesignId(null);
  };
  const handleColorChange = (colorId: string) => {
    const currentProductKey = `${selectedProductId}-${selectedColorId}`;
    const newProductKey = `${selectedProductId}-${colorId}`;
    // Always transfer designs to the new color, even if empty
    setDesigns(prev => {
      const currentDesigns = prev[currentProductKey] || {
        front: [],
        back: []
      };
      return {
        ...prev,
        [newProductKey]: {
          front: currentDesigns.front || [],
          back: currentDesigns.back || []
        }
      };
    });
    const hasDesigns = designs[currentProductKey]?.front && designs[currentProductKey].front.length > 0 || designs[currentProductKey]?.back && designs[currentProductKey].back.length > 0;
    if (hasDesigns) {
      addDebugLog('info', `Color changed to ${colorId}, designs transferred automatically`);
    }
    setSelectedColorId(colorId);
  };
  const handleImageUpload = async (imageData: string) => {
    const artworkId = await createArtwork(imageData);
    setPendingDesign({
      imageData,
      artworkId
    });
    setPrintSizeModalOpen(true);
  };
  const handleArtworkSelect = (imageData: string) => {
    setPendingDesign({
      imageData
    });
    setPrintSizeModalOpen(true);
  };
  const handlePrintSizeConfirm = (printSize: number, authorized: boolean) => {
    if (!pendingDesign) return;
    const newDesign: Design = {
      id: `design-${Date.now()}-${Math.random()}`,
      image: pendingDesign.imageData,
      x: 0,
      y: 0,
      scale: 0.8,
      zIndex: currentDesigns.length,
      artworkId: pendingDesign.artworkId,
      printSize,
      resolutionFixAuthorized: authorized
    };
    setDesigns(prev => ({
      ...prev,
      [productKey]: {
        front: prev[productKey]?.front || [],
        back: prev[productKey]?.back || [],
        [currentView]: [...currentDesigns, newDesign]
      }
    }));
    setSelectedDesignId(newDesign.id);
    setPendingDesign(null);
    if (authorized) {
      addDebugLog('success', `Design added with print size ${printSize}" (resolution fix authorized for $35)`);
    } else {
      addDebugLog('success', `Design added with print size ${printSize}"`);
    }
    if (showArtworkLibrary) {
      setShowArtworkLibrary(false);
    }
  };
  const handleScaleChange = (scale: number) => {
    if (!selectedDesign) return;
    setDesigns(prev => ({
      ...prev,
      [productKey]: {
        ...prev[productKey],
        [currentView]: currentDesigns.map(d => d.id === selectedDesignId ? {
          ...d,
          scale
        } : d)
      }
    }));
  };
  const handlePositionChange = (designId: string, x: number, y: number) => {
    setDesigns(prev => ({
      ...prev,
      [productKey]: {
        ...prev[productKey],
        [currentView]: currentDesigns.map(d => d.id === designId ? {
          ...d,
          x,
          y
        } : d)
      }
    }));
  };
  const handleRemoveDesign = (designId: string) => {
    setDesigns(prev => ({
      ...prev,
      [productKey]: {
        ...prev[productKey],
        [currentView]: currentDesigns.filter(d => d.id !== designId)
      }
    }));
    if (selectedDesignId === designId) {
      setSelectedDesignId(null);
    }
  };
  const handleRemoveBackground = async (designId: string) => {
    const design = currentDesigns.find(d => d.id === designId);
    if (!design) return;
    addDebugLog('info', '🚀 Starting background removal with remove.bg...');
    try {
      addDebugLog('info', 'Preparing image data...');
      const base64Data = design.image.split(',')[1];
      addDebugLog('success', `Image data prepared: ${(base64Data.length / 1024).toFixed(2)} KB`);
      addDebugLog('info', '📤 Sending request to remove.bg API...');
      const response = await fetch('https://api.remove.bg/v1.0/removebg', {
        method: 'POST',
        headers: {
          'X-Api-Key': REMOVEBG_API_KEY,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          image_file_b64: base64Data,
          size: 'auto',
          format: 'png'
        })
      });
      addDebugLog('data', `Response status: ${response.status} ${response.statusText}`);
      if (!response.ok) {
        const errorText = await response.text();
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch {
          errorData = {
            message: errorText
          };
        }
        addDebugLog('error', 'Remove.bg API error', errorData);
        if (response.status === 403) {
          throw new Error('Invalid API key. Please check your remove.bg API key.');
        } else if (response.status === 402) {
          throw new Error('Insufficient credits. Please check your remove.bg account.');
        } else {
          throw new Error(errorData.errors?.[0]?.title || errorData.message || 'API request failed');
        }
      }
      addDebugLog('success', '✓ Background removed successfully!');
      addDebugLog('info', 'Processing response...');
      const imageBlob = await response.blob();
      addDebugLog('success', `Processed image received: ${(imageBlob.size / 1024).toFixed(2)} KB`);
      const reader = new FileReader();
      reader.onloadend = () => {
        const processedImage = reader.result as string;
        const img = new Image();
        img.onload = () => {
          const widthInches = (img.width / 300).toFixed(2);
          const heightInches = (img.height / 300).toFixed(2);
          addDebugLog('data', '📏 Print Specifications', {
            dpi: '300 DPI',
            dimensions: `${img.width} x ${img.height} pixels`,
            printSize: `${widthInches}" x ${heightInches}" at 300 DPI`
          });
          addDebugLog('success', '✨ Background removal complete! Updating design...');
          if (design.artworkId) {
            addVersionToArtwork(design.artworkId, processedImage, 'remove_background');
          }
        };
        img.src = processedImage;
        setDesigns(prev => ({
          ...prev,
          [productKey]: {
            ...prev[productKey],
            [currentView]: currentDesigns.map(d => d.id === designId ? {
              ...d,
              image: processedImage
            } : d)
          }
        }));
      };
      reader.readAsDataURL(imageBlob);
    } catch (error) {
      addDebugLog('error', 'Background removal failed', {
        error: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined
      });
      alert(`Failed to remove background: ${error instanceof Error ? error.message : 'Unknown error'}`);
      throw error;
    }
  };
  const handleUpscaleImage = async (designId: string) => {
    const design = currentDesigns.find(d => d.id === designId);
    if (!design) return;
    addDebugLog('info', '🚀 Starting image upscaling to 4K...');
    try {
      addDebugLog('info', 'Preparing image data for upscaling...');
      const base64Data = design.image.split(',')[1];
      addDebugLog('success', `Image data prepared: ${(base64Data.length / 1024).toFixed(2)} KB`);
      addDebugLog('info', '📤 Sending upscale request to remove.bg API...');
      const response = await fetch('https://api.remove.bg/v1.0/removebg', {
        method: 'POST',
        headers: {
          'X-Api-Key': REMOVEBG_API_KEY,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          image_file_b64: base64Data,
          size: '4k',
          format: 'png'
        })
      });
      addDebugLog('data', `Response status: ${response.status} ${response.statusText}`);
      if (!response.ok) {
        const errorText = await response.text();
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch {
          errorData = {
            message: errorText
          };
        }
        addDebugLog('error', 'Upscale API error', errorData);
        throw new Error(errorData.errors?.[0]?.title || errorData.message || 'Upscale failed');
      }
      addDebugLog('success', '✓ Image upscaled successfully!');
      addDebugLog('info', 'Processing upscaled image...');
      const imageBlob = await response.blob();
      addDebugLog('success', `Upscaled image received: ${(imageBlob.size / 1024).toFixed(2)} KB`);
      const reader = new FileReader();
      reader.onloadend = () => {
        const processedImage = reader.result as string;
        const img = new Image();
        img.onload = () => {
          const widthInches = (img.width / 300).toFixed(2);
          const heightInches = (img.height / 300).toFixed(2);
          addDebugLog('data', '📏 Print Specifications', {
            dpi: '300 DPI',
            dimensions: `${img.width} x ${img.height} pixels`,
            printSize: `${widthInches}" x ${heightInches}" at 300 DPI`
          });
          addDebugLog('success', '✨ Upscaling complete! Updating design...');
          if (design.artworkId) {
            addVersionToArtwork(design.artworkId, processedImage, 'upscale');
          }
        };
        img.src = processedImage;
        setDesigns(prev => ({
          ...prev,
          [productKey]: {
            ...prev[productKey],
            [currentView]: currentDesigns.map(d => d.id === designId ? {
              ...d,
              image: processedImage
            } : d)
          }
        }));
      };
      reader.readAsDataURL(imageBlob);
    } catch (error) {
      addDebugLog('error', 'Upscaling failed', {
        error: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined
      });
      alert(`Failed to upscale image: ${error instanceof Error ? error.message : 'Unknown error'}`);
      throw error;
    }
  };
  const handleAutoTrim = async (designId: string) => {
    const design = currentDesigns.find(d => d.id === designId);
    if (!design) return;
    addDebugLog('info', '✂️ Starting auto-trim to remove dead space...');
    try {
      addDebugLog('info', 'Loading image to canvas...');
      const img = new Image();
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        img.src = design.image;
      });
      addDebugLog('success', `Image loaded: ${img.width}x${img.height}px`);
      addDebugLog('info', 'Analyzing image to find content bounds...');
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d')!;
      ctx.drawImage(img, 0, 0);
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const pixels = imageData.data;
      let minX = canvas.width;
      let minY = canvas.height;
      let maxX = 0;
      let maxY = 0;
      for (let y = 0; y < canvas.height; y++) {
        for (let x = 0; x < canvas.width; x++) {
          const index = (y * canvas.width + x) * 4;
          const alpha = pixels[index + 3];
          if (alpha > 10) {
            minX = Math.min(minX, x);
            minY = Math.min(minY, y);
            maxX = Math.max(maxX, x);
            maxY = Math.max(maxY, y);
          }
        }
      }
      const padding = 5;
      minX = Math.max(0, minX - padding);
      minY = Math.max(0, minY - padding);
      maxX = Math.min(canvas.width, maxX + padding);
      maxY = Math.min(canvas.height, maxY + padding);
      const trimWidth = maxX - minX;
      const trimHeight = maxY - minY;
      addDebugLog('success', `Content bounds found: ${trimWidth}x${trimHeight}px`, {
        original: `${img.width}x${img.height}`,
        trimmed: `${trimWidth}x${trimHeight}`,
        removed: `${img.width - trimWidth}x${img.height - trimHeight}`
      });
      if (trimWidth <= 0 || trimHeight <= 0) {
        addDebugLog('error', 'No content found in image');
        throw new Error('No content found to trim');
      }
      addDebugLog('info', 'Creating trimmed image...');
      const trimmedCanvas = document.createElement('canvas');
      trimmedCanvas.width = trimWidth;
      trimmedCanvas.height = trimHeight;
      const trimmedCtx = trimmedCanvas.getContext('2d')!;
      trimmedCtx.drawImage(canvas, minX, minY, trimWidth, trimHeight, 0, 0, trimWidth, trimHeight);
      const trimmedImage = trimmedCanvas.toDataURL('image/png');
      const widthInches = (trimWidth / 300).toFixed(2);
      const heightInches = (trimHeight / 300).toFixed(2);
      addDebugLog('data', '📏 Print Specifications', {
        dpi: '300 DPI',
        dimensions: `${trimWidth} x ${trimHeight} pixels`,
        printSize: `${widthInches}" x ${heightInches}" at 300 DPI`
      });
      addDebugLog('success', '✨ Auto-trim complete! Updating design...');
      if (design.artworkId) {
        addVersionToArtwork(design.artworkId, trimmedImage, 'trim');
      }
      setDesigns(prev => ({
        ...prev,
        [productKey]: {
          ...prev[productKey],
          [currentView]: currentDesigns.map(d => d.id === designId ? {
            ...d,
            image: trimmedImage
          } : d)
        }
      }));
    } catch (error) {
      addDebugLog('error', 'Auto-trim failed', {
        error: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined
      });
      alert(`Failed to trim image: ${error instanceof Error ? error.message : 'Unknown error'}`);
      throw error;
    }
  };
  const currentImageUrl = currentView === 'back' && currentVariant?.backImageUrl ? currentVariant.backImageUrl : currentVariant?.imageUrl || '';
  return <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex flex-col" data-id="element-0">
      <header className="bg-white border-b border-gray-200 px-8 py-6" data-id="element-1">
        <div className="flex items-center justify-between" data-id="element-2">
          <div data-id="element-3">
            <h1 className="text-2xl font-bold text-gray-900" data-id="element-4">
              Apparel Designer
            </h1>
            <p className="text-sm text-gray-500 mt-1" data-id="element-5">
              Select a product, upload your designs, and customize
            </p>
          </div>

          <button onClick={() => setShowShoppingBag(true)} className="relative px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors flex items-center gap-2" data-id="element-6">
            <ShoppingBagIcon className="w-5 h-5" data-id="element-7" />
            <span data-id="element-8">Shopping Bag</span>
            {shoppingBag.length > 0 && <span className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center" data-id="element-9">
                {shoppingBag.length}
              </span>}
          </button>
        </div>
      </header>

      <div className="flex-1 flex" data-id="element-10">
        <aside className="w-64 bg-white border-r border-gray-200 p-6" data-id="element-11">
          <ProductSelector products={products.map(p => ({
          id: p.id,
          name: p.name,
          icon: p.icon
        }))} selectedProduct={selectedProductId} onSelectProduct={handleProductChange} data-id="element-12" />
        </aside>

        <main className="flex-1 p-12 flex flex-col" data-id="element-13">
          <ViewToggle currentView={currentView} onViewChange={setCurrentView} showBackView={currentProduct.hasBackView} data-id="element-14" />

          {currentVariant && currentColor && <ProductDisplay imageUrl={currentImageUrl} productName={currentProduct.name} colorName={currentColor.name} currentView={currentView} designs={currentDesigns} selectedDesignId={selectedDesignId} onDesignSelect={setSelectedDesignId} onDesignPositionChange={handlePositionChange} data-id="element-15" />}
        </main>

        <aside className="w-80 bg-white border-l border-gray-200 p-6 overflow-y-auto" data-id="element-16">
          <div className="flex gap-2 mb-4" data-id="element-17">
            <button onClick={() => setShowArtworkLibrary(false)} className={`flex-1 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${!showArtworkLibrary ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-18">
              Upload New
            </button>
            <button onClick={() => setShowArtworkLibrary(true)} className={`flex-1 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${showArtworkLibrary ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-19">
              My Artwork ({artworkLibrary.length})
            </button>
          </div>

          {showArtworkLibrary ? <ArtworkLibrary artworks={artworkLibrary} onSelectArtwork={handleArtworkSelect} data-id="element-20" /> : <>
              <ImageUploader onImageUpload={handleImageUpload} hasImage={currentDesigns.length > 0} data-id="element-21" />

              <DesignControls designs={currentDesigns} selectedDesignId={selectedDesignId} selectedDesign={selectedDesign} onDesignSelect={setSelectedDesignId} onScaleChange={handleScaleChange} onRemoveDesign={handleRemoveDesign} onRemoveBackground={handleRemoveBackground} onUpscaleImage={handleUpscaleImage} onAutoTrim={handleAutoTrim} data-id="element-22" />
            </>}
        </aside>
      </div>

      <footer className="bg-white border-t border-gray-200 px-8 py-6" data-id="element-23">
        <div className="flex items-center justify-between" data-id="element-24">
          <ColorSelector colors={currentProduct.colors} selectedColor={selectedColorId} onSelectColor={handleColorChange} data-id="element-25" />

          {hasAnyDesigns && <button onClick={() => setAddToBagModalOpen(true)} className="px-6 py-3 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg transition-colors flex items-center gap-2" data-id="element-26">
              <ShoppingBagIcon className="w-5 h-5" data-id="element-27" />
              Add to Shopping Bag
            </button>}
        </div>
      </footer>

      <DebugPanel logs={debugLogs} onClear={() => setDebugLogs([])} isMinimized={isDebugMinimized} onToggleMinimize={() => setIsDebugMinimized(!isDebugMinimized)} data-id="element-28" />

      {pendingDesign && <PrintSizeModal isOpen={printSizeModalOpen} onClose={() => {
      setPrintSizeModalOpen(false);
      setPendingDesign(null);
    }} onConfirm={handlePrintSizeConfirm} imageDimensions={(() => {
      const img = new Image();
      img.src = pendingDesign.imageData;
      return {
        width: img.naturalWidth || 1000,
        height: img.naturalHeight || 1000
      };
    })()} currentPrintSize={undefined} currentAuthorization={false} data-id="element-29" />}

      <AddToBagModal isOpen={addToBagModalOpen} onClose={() => setAddToBagModalOpen(false)} onConfirm={handleAddToBag} productName={`${currentColor?.name} ${currentProduct.name}`} data-id="element-30" />

      <ShoppingBag isOpen={showShoppingBag} onClose={() => setShowShoppingBag(false)} items={shoppingBag} onEditItem={handleEditBagItem} onRemoveItem={handleRemoveBagItem} onUpdateSizeQuantities={handleUpdateBagItemSizeQuantities} onProceedToCheckout={handleProceedToCheckout} data-id="element-31" />

      <Checkout isOpen={showCheckout} onClose={() => setShowCheckout(false)} items={shoppingBag} onOrderComplete={handleOrderComplete} data-id="element-32" />
    </div>;
}